import * as React from "react";
import styled from "@emotion/styled";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  AllCaps,
  Box,
  Container,
  FlexRow,
  H2,
  HeadingGroup,
  Link,
  Modal,
  NoBreak,
  PageHeader,
  SiteContainer,
  Stack,
  Text,
} from "atoms";
import { trackEvent } from "contexts/mixpanel";
import { useAuth } from "features/auth";
import { useCollective } from "features/collectives";
import { Head } from "features/misc";
import { useProfile } from "features/profile";
import { ArtistApplicationForm } from "features/reviewable";
import { paths } from "utils/paths";

const REFERRAL_ID_PARAM = "code";
const REFERRAL_ID_KEY = "HUG_artist_referral_id";
const APPLICATION_BYPASS_PARAM = "collective";

const trackSkip = () =>
  trackEvent({
    name: "Button Click",
    ButtonContext: "Create Artist Application",
    ButtonName: "Skip Application",
  });

function CreateArtistApplication() {
  // Get referrer's ID from search params or local storage
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get(REFERRAL_ID_PARAM);
  const storedId = window.localStorage.getItem(REFERRAL_ID_KEY);
  const referralId = urlId ?? storedId ?? undefined;

  const bypassId = searchParams.get(APPLICATION_BYPASS_PARAM) ?? undefined;

  const { data: collective } = useCollective(bypassId?.split("~")[0]);

  const { user } = useAuth();
  const location = useLocation();

  // Did user just come from the registration form on the logged out home page?
  const [isNewAccount, setIsNewAccount] = React.useState<boolean>(
    location.state?.newAccount ?? false,
  );
  const hideIsNewAccountMsg = React.useCallback(
    () => setIsNewAccount(false),
    [setIsNewAccount],
  );

  React.useEffect(() => {
    if (urlId) {
      // Store the referrer's ID in case the invited user leaves this page and returns
      window.localStorage.setItem(REFERRAL_ID_KEY, urlId);
      // Remove the ID from the URL to clean it up
      searchParams.delete(REFERRAL_ID_PARAM);
      setSearchParams(searchParams);
    }
  }, [urlId, searchParams, setSearchParams]);

  return (
    <>
      <Head />

      {!user && (
        <Modal
          hideCloseButton
          style={{
            maxWidth: 460,
            marginInline: 0,
            minWidth: 300,
            height: "max-content",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            position: "absolute",
          }}
        >
          <Stack gap="sm">
            <Stack gap="sm">
              <Stack gap="sm">
                <H2 size="md" textTransform="none" textAlign="center">
                  You must have a <AllCaps>Hug</AllCaps> account to{" "}
                  <NoBreak>apply for an Artist Profile</NoBreak>
                </H2>
                <Stack gap="xxs">
                  <Text bold size="xs" textAlign="center">
                    Sign in or create an account to continue.
                  </Text>
                  <Text size="xxs" textAlign="center">
                    It takes about 5 minutes to create{" "}
                    <NoBreak>an account and apply.</NoBreak>
                  </Text>
                </Stack>
              </Stack>

              <FlexRow itemsFlex="1 0 0%" flexWrap="wrap" gap="10px">
                <Link
                  to="/sign-in"
                  variant="primary"
                  state={{ from: location }}
                >
                  Sign&nbsp;In
                </Link>
                <Link
                  to="/register"
                  variant="primary"
                  state={{ from: location }}
                >
                  Create&nbsp;Account
                </Link>
              </FlexRow>
            </Stack>
            <FlexRow as="span" justifyContent="center">
              <Link to={paths.artistProfileBenefits()} size="xxs">
                Cancel
              </Link>
            </FlexRow>
          </Stack>
        </Modal>
      )}

      <PageHeader brush="1" bg="black" overlap>
        <Container width="sm">
          <Stack gap="sm">
            <HeadingGroup>
              <small role="heading" aria-level={1}>
                {isNewAccount ? "Let's Jump Right In" : "My Artist Application"}
              </small>

              {/* Show the Collective Invite headline even if `referralId` exists. */}
              {collective && (
                <Text size="xxl" bold font="display" textTransform="uppercase">
                  {collective.name} invited you to join their collective
                  on&nbsp;
                  <AllCaps>Hug</AllCaps>
                </Text>
              )}

              {referralId && !bypassId && <RefererText id={referralId} />}

              {!referralId && !bypassId && (
                <Text size="h2" bold font="display" textTransform="uppercase">
                  <span>{isNewAccount ? "Apply For An" : "Get Your"}</span>
                  <span>&nbsp;Artist&nbsp;Profile</span>
                </Text>
              )}
            </HeadingGroup>

            <Container width="xs">
              <Text size="xs">
                A <AllCaps>Hug</AllCaps> Artist&nbsp;Profile is the ultimate
                tool to grow your audience and connect with artists and art
                lovers like never before.{" "}
                <Link to={paths.artistProfileBenefits()}>
                  Learn&nbsp;more&nbsp;about&nbsp;the&nbsp;benefits
                </Link>
                &#8288;.
              </Text>
            </Container>
          </Stack>
        </Container>

        {isNewAccount && (
          <Container width="xs">
            <DarkBox>
              <Text>No thanks. Maybe later!</Text>
              <Link
                to="/"
                variant="secondary"
                arrow="r"
                size="xs"
                onClick={trackSkip}
              >
                Start Exploring
              </Link>
            </DarkBox>
          </Container>
        )}
      </PageHeader>

      <SiteContainer width="xs">
        <ArtistApplicationForm
          referralId={referralId}
          applicationBypassId={bypassId}
          collectiveName={collective?.name}
          onSuccess={hideIsNewAccountMsg}
        />
      </SiteContainer>
    </>
  );
}

function RefererText({ id }: { id: string }) {
  const { data } = useProfile(id);

  return (
    <Text size="xxl" bold font="display" textTransform="uppercase">
      {data?.name ? (
        <span>{data.name} invited&nbsp;you</span>
      ) : (
        <span>You&apos;ve been invited</span>
      )}
      <br />
      <span>
        to&nbsp;apply for a <AllCaps>Hug</AllCaps>&nbsp;Artist&nbsp;Profile!
      </span>
    </Text>
  );
}

const DarkBox = styled(Box)(({ theme }) => ({
  alignItems: "center",
  background: theme.colors.bg10,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.md,
  justifyContent: "center",
  margin: `${theme.spacing.lg} ${theme.spacing.lg} 0`,
  padding: theme.spacing.md,
  p: {
    fontWeight: "bold",
    margin: 0,
  },
  [theme.breakpoints.tablet]: {
    alignItems: "baseline",
    flexDirection: "row",
    gap: theme.spacing.lg,
  },
}));

export default CreateArtistApplication;
